/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import {
  Typography,
  Skeleton,
  IconButton as IconButtons,
  Paper,
  InputBase,
  Chip,
  Avatar,
  IconButton,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ForumIcon from "@mui/icons-material/Forum";
import { debounce } from "lodash";
import { DEBOUNCE_TIME } from "../../../utils/constants";
import { callGetChatUsers } from "../../../actions/chat";
import ChatIconButton from "../../../utils/ChatIconButton";
import { getColorProfile } from "../../../utils/getColorProfile";
import { userTypes } from "../../../utils/ChatUserTypes";
import NoDataLabel from "../../../components/NoDataLabel";
import useLocalization from "../../../hooks/useLocalization";

const debounceChatUserSearch = debounce(
  ({ value, dispatch, pageNo, userType }) => {
    value.length > 0 &&
      dispatch(callGetChatUsers({ name: value, pageNo, userType }));
  },
  DEBOUNCE_TIME
);

const UserSearch = ({
  tabValue,
  setOpenChatWindowChild,
  setSelectedContact,
}) => {
  const dispatch = useDispatch();
  // const { t } = useLocalization("chat");
  const [searchUser, setSearchUser] = useState("");
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const showSearchModal = searchModalOpen && searchUser?.length > 0;

  const handleSearchContact = (e) => {
    const value = e.target.value;
    setSearchUser(value);
    setSearchModalOpen(true);
    debounceChatUserSearch({
      value,
      pageNo: 1,
      userType: userTypes?.[tabValue],
      dispatch,
    });
  };

  useEffect(() => {
    return () => {
      dispatch({
        type: "GET_CHAT_USERS_SUC",
        payload: {},
      });
    };
  }, []);

  return (
    <Box sx={{ position: "relative", px: 1 }}>
      <Box
        component="form"
        sx={{
          display: "flex",
          alignItems: "center",
          mt: 1,
          background: "#EEF3F7",
          borderRadius: "4px",
        }}
      >
        <IconButton
          type="button"
          sx={{ paddingY: "8px" }}
          aria-label={`Search`}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
        <InputBase
          value={searchUser}
          onChange={handleSearchContact}
          sx={{ ml: 0.5, flex: 1, fontSize: "14px" }}
          inputProps={{ "aria-label": "search" }}
          placeholder={`Search`}
        />
        {searchUser?.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <IconButtons onClick={() => setSearchUser("")}>
              <CloseIcon fontSize="small" />
            </IconButtons>
          </Box>
        )}
      </Box>
      {showSearchModal && (
        <RenderDetails
          setSelectedContact={setSelectedContact}
          setSearchUser={setSearchUser}
          setOpenChatWindowChild={setOpenChatWindowChild}
          setSearchModalOpen={setSearchModalOpen}
        />
      )}
    </Box>
  );
};

export default UserSearch;

const RenderDetails = ({
  setSearchUser,
  setOpenChatWindowChild,
  setSelectedContact,
  setSearchModalOpen,
}) => {
  const dispatch = useDispatch();
  // const { t } = useLocalization("chat");
  const { getChatUsersLoading, getChatUsers, chatUserError } = useSelector(
    (state) => state.chat
  );
  const [loading, setLoading] = useState(false);

  const chatUserData = getChatUsers?.data;

  const handleAdd = (userData) => {
    setSelectedContact(userData);
    setOpenChatWindowChild(true);
    setSearchUser("");
    setSearchModalOpen(false);
  };

  const handleMessage = (userData) => {
    setSelectedContact({
      ...userData,
      id: userData?.conversation_id,
      sender: {
        ...userData?.user_detail,
        user_type: userData?.user_type,
        status: userData?.status,
        visibility: userData?.visibility,
        status_note: userData?.status_note,
      },
    });
    setOpenChatWindowChild(true);
    setSearchUser("");
    setSearchModalOpen(false);
  };

  useEffect(() => {
    if (chatUserError) {
      setLoading(false);
    } else if (!chatUserData || getChatUsersLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [chatUserData, getChatUsersLoading, getChatUsersLoading]);

  const showData = (chatUserData && getChatUsers?.data?.length > 0) || loading;
  const themebody = useTheme()?.palette?.primary;
  const renderLoader = (
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
        width: "100%",
        px: 1,
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        marginTop: "6px",
      }}
    >
      <Skeleton height="40px" animation="wave" />
      <Skeleton height="40px" animation="wave" />
      <Skeleton height="40px" animation="wave" />
      <Skeleton height="40px" animation="wave" />
      <Skeleton height="40px" animation="wave" />
      <Skeleton height="40px" animation="wave" />
      <Skeleton height="40px" animation="wave" />
      <Skeleton height="40px" animation="wave" />
      <Skeleton height="40px" animation="wave" />
      <Skeleton height="40px" animation="wave" />
      <Skeleton height="40px" animation="wave" />
    </Box>
  );

  const renderEmptyData = (
    <Box mt={2}>
      <NoDataLabel />
    </Box>
  );

  const renderData = (
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
        marginBottom: "50px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        paddingX: "10px",
      }}
    >
      {chatUserData?.map((user) => {
        const {
          user_detail: {
            first_name = "",
            last_name = "",
            email = "",
            profile_pic = "",
          } = {},
          is_added,
          chat_requested,
          status,
          invoffuserid,
          user_type,
        } = user ?? {};

        const fullName = `${first_name} ${last_name}`;
        return (
          <Box key={user?.userid}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Avatar
                  variant="contained"
                  alt={first_name}
                  src={profile_pic !== "undefined" && `${profile_pic}`}
                  sx={{
                    fontSize: "14px",
                    width: "32px",
                    height: "32px",
                    background: getColorProfile({ first_name, last_name }),
                  }}
                >
                  {first_name?.[0]?.toUpperCase() +
                    last_name?.[0]?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography
                    sx={{ fontSize: "0.8rem", overflowWrap: "anywhere" }}
                  >
                    {fullName}{" "}
                    {user_type && (
                      <Typography
                        color={themebody?.secondarybordercolor}
                        fontSize={"0.8rem"}
                        component="span"
                      >
                        ({user_type})
                      </Typography>
                    )}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: themebody?.textcolor,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "150px",
                    }}
                  >
                    {email}
                  </Typography>
                </Box>
              </Box>
              {is_added || !chat_requested ? (
                <Box sx={{ mr: "10px" }}>
                  {is_added ? (
                    <ChatIconButton
                      onClick={() => handleMessage(user)}
                      icon={
                        <ForumIcon
                          sx={{
                            color: themebody?.secondaryButton?.iconcolor,
                            fontSize: "0.8rem",
                            fontWeight:
                              themebody?.secondaryButton?.iconfontWeight,
                          }}
                        />
                      }
                      padding="2px 10px 2px 4px"
                      fontSize={"0.6rem"}
                      borderColor={themebody?.secondaryButton?.borderColor}
                      background={themebody?.secondaryButton?.bgColor}
                      fontFamily={themebody?.fontBoldFamily}
                      color={themebody?.secondaryButton?.color}
                      label={`Message`}
                    />
                  ) : (
                    <ChatIconButton
                      onClick={() => handleAdd(user)}
                      icon={
                        <AddIcon
                          sx={{
                            color: themebody?.secondaryButton?.iconcolor,
                            fontSize: "0.9rem",
                            fontWeight:
                              themebody?.secondaryButton?.iconfontWeight,
                          }}
                        />
                      }
                      padding="2px 10px 2px 4px"
                      fontSize={"0.6rem"}
                      borderColor={themebody?.secondaryButton?.borderColor}
                      background={themebody?.secondaryButton?.bgColor}
                      fontFamily={themebody?.fontBoldFamily}
                      color={themebody?.secondaryButton?.color}
                      label={`Add`}
                    />
                  )}
                </Box>
              ) : (
                <Chip
                  sx={{
                    fontSize: "0.8rem",
                    mr: "30px",
                  }}
                  size="small"
                  variant="filled"
                  label={`Requested`}
                />
              )}
            </Box>
            <Box
              sx={{
                height: "1.5px",
                width: "96%",
                mt: "6px",
                background: themebody?.textcolor,
              }}
            ></Box>
          </Box>
        );
      })}
    </Box>
  );

  useEffect(() => {
    return () => {
      dispatch({
        type: "GET_CHAT_USERS_SUC",
        details: {},
      });
    };
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        background: "white",
        overflow: "auto",
        position: "absolute",
        left: "0px",
        zIndex: 50,
        right: 0,
        mt: "8px",
      }}
    >
      {showData ? (loading ? renderLoader : renderData) : renderEmptyData}
    </Box>
  );
};
